<script>
import { mapState, mapGetters } from "vuex";

// Descriptions for each permission entity
const DESCRIPTIONS = {
  alerts: "Create, view, edit custom hashrate alerts",
  team: "Edit team settings, change team name or handle, delete team",
  roles: "Create, view, edit roles, assign roles to team members",
  members: "View, invite, and remove team members",
  workers: "Manage workers and view their data",
  groups: "Manage groups and view their data",
  billing: "Manage team license and billing information",
};

export default {
  props: {
    // Value of permission data
    value: {
      type: Object,
      default() {
        return {
          "team.manage": false,
          "roles.manage": false,
          "billing.manage": false,
          "members.view": false,
          "members.edit": false,
          "workers.view": false,
          "workers.edit": false,
          "groups.view": false,
          "groups.edit": false,
          "alerts.view": false,
          "alerts.edit": false,
        };
      },
    },

    // Whether the table is disabled
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      // Values of each of the current read only toggles
      readOnlyToggles: {
        members: false,
        workers: false,
        groups: false,
        alerts: false,
      },
    };
  },

  computed: {
    // List of options for the permissions table
    permissionOptions() {
      let options = {};
      Object.values(this.permissions).forEach((permission) => {
        if (!(permission.entity in options)) {
          options[permission.entity] = {
            name: permission.name,
            entity: permission.entity,
            description: DESCRIPTIONS[permission.entity],
            id: permission.id,
            priority: permission.priority,
          };
        }
        options[permission.entity][permission.action] = permission;
      });
      return options;
    },

    // Mapped vuex state
    ...mapGetters(["can"]),
    ...mapState(["roles", "groups", "permissions"]),
  },

  watch: {
    // When the value changes, update the read only toggles to match
    value: {
      deep: true,
      handler(value) {
        this.readOnlyToggles = {
          members: value["members.view"] && !value["members.edit"],
          workers: value["workers.view"] && !value["workers.edit"],
          groups: value["groups.view"] && !value["groups.edit"],
          alerts: value["alerts.view"] && !value["alerts.edit"],
        };
      },
    },
  },

  methods: {
    // When a checkbox turns off, make sure to turn off the read only
    // toggle
    updateCheck(entity) {
      this.value[entity + ".view"] = !this.value[entity + ".view"];
      if (this.value[entity + ".view"]) {
        this.value[entity + ".edit"] = this.can[entity].edit;
      } else {
        this.value[entity + ".edit"] = false;
      }
    },

    // When a read only toggle is turned on, make sure to turn on the
    // checkbox
    updateToggle(entity) {
      if (this.readOnlyToggles[entity]) {
        this.value[entity + ".edit"] = false;
        this.value[entity + ".view"] = true;
      } else {
        this.value[entity + ".edit"] = true;
        this.value[entity + ".view"] = true;
      }
    },
  },
};
</script>

<template>
  <b-table
    responsive
    small
    :fields="[
      {
        key: 'name',
        label: 'Permission',
      },
      {
        key: 'description',
        label: 'Description',
        tdClass: ['d-none', 'd-lg-block'],
        thClass: ['d-none', 'd-lg-block'],
      },
      {
        key: 'access',
        label: 'Access',
      },
    ]"
    :items="Object.values(permissionOptions)"
    sortBy="priority"
  >
    <template #cell(access)="{ item }">
      <b-form-checkbox
        v-if="item.manage"
        class="d-inline-block"
        size="lg"
        :disabled="disabled"
        v-model="value[item.entity + '.manage']"
      ></b-form-checkbox>
      <b-form-checkbox
        v-if="item.view"
        class="d-inline-block"
        size="lg"
        :disabled="disabled"
        v-model="value[item.entity + '.view']"
        @change="updateCheck(item.entity)"
      ></b-form-checkbox>
      <div
        v-if="item.edit"
        class="d-inline-block custom-control custom-switch b-custom-control-lg"
      >
        <input
          type="checkbox"
          class="custom-control-input d-inline-block"
          size="lg"
          :disabled="disabled"
          v-model="readOnlyToggles[item.entity]"
          @change="updateToggle(item.entity)"
          :id="'read-only' + item.entity"
        />
        <label class="custom-control-label" :for="'read-only' + item.entity">
          Read Only
        </label>
      </div>
    </template>
  </b-table>
</template>

<style scoped>
.notes {
  color: #6c757d;
}
</style>
